// components/PhoneVerification.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Verified as VerifiedIcon,
  Send as SendIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../services/api';

const PhoneVerification = ({ language, onVerified, currentPhoneNumber }) => {
  const [phoneNumber, setPhoneNumber] = useState(currentPhoneNumber || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('input'); // 'input', 'verify', 'verified'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [verificationStatus, setVerificationStatus] = useState({
    phoneNumber: '',
    isVerified: false
  });

  // Check current verification status on mount
  useEffect(() => {
    checkStatus();
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const checkStatus = async () => {
    try {
      const response = await api.get('/phone-verification/status');
      setVerificationStatus(response.data);
      if (response.data.isVerified) {
        setStep('verified');
      }
    } catch (err) {
      console.error('Status check error:', err);
    }
  };

  const handleSendCode = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/phone-verification/send-code', {
        phoneNumber: phoneNumber || currentPhoneNumber
      });

      setSuccess('Verification code sent! Check your phone.');
      setStep('verify');
      setCountdown(60); // 60 second cooldown for resend
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setLoading(true);
    setError('');

    try {
      await api.post('/phone-verification/verify-code', {
        code: verificationCode
      });

      setSuccess('Phone number verified successfully!');
      setStep('verified');
      setVerificationStatus({
        phoneNumber: phoneNumber || currentPhoneNumber,
        isVerified: true
      });
      
      if (onVerified) {
        onVerified();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    setError('');

    try {
      await api.post('/phone-verification/resend');
      setSuccess('New verification code sent!');
      setCountdown(60);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'verified') {
    return (
      <Paper sx={{ 
        p: 3, 
        backgroundColor: '#0D0D0D',
        border: '1px solid #4CAF50',
        borderRadius: '12px',
        mb: 2
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <VerifiedIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#4CAF50', mb: 1 }}>
            {language === 'ar' ? 'تم توثيق رقم الهاتف' : 'Phone Verified'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
            {verificationStatus.phoneNumber || currentPhoneNumber}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ 
      p: 3, 
      backgroundColor: '#0D0D0D',
      border: '1px solid #2D2D2D',
      borderRadius: '12px',
      mb: 2
    }}>
      <Typography variant="h6" sx={{ color: '#FFD700', mb: 2 }}>
        {language === 'ar' ? 'توثيق رقم الهاتف' : 'Phone Verification'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, backgroundColor: '#1A1A1A', color: '#FFFFFF' }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, backgroundColor: '#1A1A1A', color: '#FFFFFF' }}>
          {success}
        </Alert>
      )}

      {step === 'input' ? (
        <Box>
          <TextField
            fullWidth
            label={language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+201234567890"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: '#B3B3B3' }} />
                </InputAdornment>
              ),
              sx: { color: '#FFFFFF' }
            }}
            InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            sx={{ mb: 2 }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleSendCode}
            disabled={loading || !phoneNumber}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              '&:hover': { backgroundColor: '#FFE55C' },
              '&.Mui-disabled': {
                backgroundColor: '#2D2D2D',
                color: '#6B6B6B'
              }
            }}
          >
            {loading 
              ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...')
              : (language === 'ar' ? 'إرسال رمز التحقق' : 'Send Verification Code')
            }
          </Button>
        </Box>
      ) : (
        <Box>
          <TextField
            fullWidth
            label={language === 'ar' ? 'رمز التحقق' : 'Verification Code'}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.slice(0, 6))}
            placeholder="123456"
            required
            inputProps={{ maxLength: 6 }}
            InputProps={{
              sx: { color: '#FFFFFF' }
            }}
            InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            sx={{ mb: 2 }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleVerifyCode}
            disabled={loading || verificationCode.length !== 6}
            startIcon={loading ? <CircularProgress size={20} /> : <VerifiedIcon />}
            sx={{
              backgroundColor: '#4CAF50',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#45a049' },
              '&.Mui-disabled': {
                backgroundColor: '#2D2D2D',
                color: '#6B6B6B'
              },
              mb: 1
            }}
          >
            {loading 
              ? (language === 'ar' ? 'جاري التحقق...' : 'Verifying...')
              : (language === 'ar' ? 'تأكيد الرمز' : 'Verify Code')
            }
          </Button>

          <Button
            fullWidth
            variant="text"
            disabled={countdown > 0 || loading}
            onClick={handleResend}
            startIcon={<RefreshIcon />}
            sx={{ 
              color: countdown > 0 ? '#6B6B6B' : '#FFD700',
              '&:hover': {
                backgroundColor: countdown > 0 ? 'transparent' : 'rgba(255, 215, 0, 0.08)'
              }
            }}
          >
            {countdown > 0 
              ? (language === 'ar' ? `إعادة الإرسال بعد ${countdown} ثانية` : `Resend in ${countdown}s`)
              : (language === 'ar' ? 'إعادة إرسال الرمز' : 'Resend Code')
            }
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default PhoneVerification;